import type { ApiErrorItem } from './types';

/**
 * ChangeAppError
 *
 * Custom error class for IAM change-app API failures.
 * Includes HTTP status, error code, and structured error items
 * from the IAM response body.
 *
 * @extends Error
 *
 * @example
 * ```ts
 * try {
 *   await sender.navigateTo(options);
 * } catch (error) {
 *   if (error instanceof ChangeAppError) {
 *     console.error(error.code, error.status, error.errors);
 *   }
 * }
 * ```
 *
 * @since 1.0.0
 * @category Errors/ChangeApp
 */
export class ChangeAppError extends Error {
  /** IAM error code identifier */
  public readonly code: string;
  /** HTTP response status code */
  public readonly status: number;
  /** Structured error items from IAM response */
  public readonly errors: ApiErrorItem[];

  constructor(
    message: string,
    status: number,
    code: string,
    errors: ApiErrorItem[] = []
  ) {
    super(message);
    this.name = 'ChangeAppError';
    this.code = code;
    this.status = status;
    this.errors = errors;

    Object.setPrototypeOf(this, ChangeAppError.prototype);
  }
}
